import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./ChatWidget.css";

gsap.registerPlugin(ScrollTrigger);

function ChatWithDogeMax() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Woof woof! Hey there, fellow Doge enthusiast! I'm DOGEMAX, your personal Dogecoin guru. Ask me anything about Dogecoin, Doginals, DRC20, Dunes, Laika L2, or the Tap on Doge Protocol!",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [aiMode, setAiMode] = useState("local");
  const [trainMode, setTrainMode] = useState(false);
  const [trainStep, setTrainStep] = useState(null);
  const [trainTrigger, setTrainTrigger] = useState("");
  const [stats, setStats] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    fetch("/api/stats")
      .then((r) => r.json())
      .then((data) => {
        setAiMode(data.mode || "local");
        setStats(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    gsap.fromTo(
      ".memecoin-dogemax",
      { opacity: 0, scale: 0.8, y: -50 },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        scrollTrigger: {
          trigger: ".memecoin-dogemax",
          start: "top 85%",
          end: "top 65%",
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      [".ChatWithDogeMax-title", ".ChatWithDogeMax-text"],
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        stagger: 0.2,
        scrollTrigger: {
          trigger: ".ChatWithDogeMax-content",
          start: "top 95%",
          end: "top 75%",
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      ".ChatWithDogeMax-content button",
      { opacity: 0, scale: 0.9 },
      {
        opacity: 1,
        scale: 1,
        duration: 3,
        scrollTrigger: {
          trigger: ".ChatWithDogeMax-content",
          start: "top 95%",
          end: "top 75%",
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      ".unique-img",
      { opacity: 0, x: 50 },
      {
        opacity: 1,
        x: 0,
        scrollTrigger: {
          trigger: ".unique",
          start: "top 85%",
          end: "top 65%",
          scrub: true,
        },
      }
    );
  }, []);

  const startTrainMode = () => {
    setTrainMode(true);
    setTrainStep("topic");
    setTrainTrigger("");
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "Woof woof! I love learning new things! What topic or question should I learn about? Just type it like you're chatting with me!",
        source: "train_prompt",
      },
    ]);
  };

  const exitTrainMode = () => {
    setTrainMode(false);
    setTrainStep(null);
    setTrainTrigger("");
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "Back to chat mode! Ask me anything about the Doge ecosystem, Shibe!",
      },
    ]);
  };

  const handleTrainFlow = async (userText) => {
    if (trainStep === "topic") {
      setTrainTrigger(userText);
      setTrainStep("answer");
      setMessages((prev) => [
        ...prev,
        { role: "user", content: userText },
        {
          role: "assistant",
          content: `Got it! So when someone asks about "${userText}", what should I say? Type the answer and I'll remember it!`,
          source: "train_prompt",
        },
      ]);
      return;
    }

    if (trainStep === "answer") {
      setMessages((prev) => [...prev, { role: "user", content: userText }]);
      setIsLoading(true);

      try {
        const response = await fetch("/api/train", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            trigger: trainTrigger,
            response: userText,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: `Woof woof! I learned something new! Now when someone asks about "${trainTrigger}", I'll know what to say! I now have ${data.totalTrained} trained responses. Want to teach me more, or switch back to chatting?`,
              source: "trained_new",
            },
          ]);
          if (stats) {
            setStats({ ...stats, trainedResponses: data.totalTrained });
          }
          setTrainStep("topic");
          setTrainTrigger("");
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                data.error ||
                "Woof! I couldn't learn that. Can you try rephrasing?",
            },
          ]);
          setTrainStep("topic");
          setTrainTrigger("");
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Woof! Had a connection hiccup. Let's try again - what should I learn about?",
          },
        ]);
        setTrainStep("topic");
        setTrainTrigger("");
      } finally {
        setIsLoading(false);
      }
      return;
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");

    if (trainMode) {
      await handleTrainFlow(userMessage);
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage, sessionId }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.reply,
            mode: data.mode,
            source: data.source,
          },
        ]);
        if (data.sessionId) setSessionId(data.sessionId);
        if (data.mode) setAiMode(data.mode);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              data.error || "Woof! Something went wrong. Please try again!",
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Woof! I seem to be having connection issues. Please try again in a moment!",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <section>
      <div className="container">
        <div className="memecoin-img">
          <img
            className="memecoin-dogemax"
            src=" /img/memecoin-img.png"
            alt="is dogemax a memecoin?"
          />
        </div>
        <div className="ChatWithDogeMax-content">
          <div className="ChatWithDogeMax-ttile-text">
            <h2 className="ChatWithDogeMax-title">Chat with DOGEMAX</h2>
            <h3 className="ChatWithDogeMax-text">
              Your Personal Dogecoin Guru
            </h3>
          </div>
          <button className="btn chat-open-btn" onClick={() => setIsOpen(true)}>
            Ask Dogemax
          </button>
        </div>
        <div className="unique">
          <img
            className="unique-img"
            src=" /img/unique.png"
            alt="what makes it unique?"
          />
        </div>
      </div>

      {isOpen && (
        <div className="chat-overlay" onClick={() => setIsOpen(false)}>
          <div className="chat-widget" onClick={(e) => e.stopPropagation()}>
            <div className="chat-header">
              <div className="chat-header-info">
                <div className="chat-avatar">
                  <img src="/img/dogecoin-logo.png" alt="DOGEMAX" />
                </div>
                <div>
                  <h3>DOGEMAX</h3>
                  <div className="chat-header-status">
                    <span className="chat-status">Online</span>
                    <span className={`chat-mode-badge chat-mode-${aiMode}`}>
                      {aiMode === "openai" ? "AI" : "Local Brain"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                <button
                  className={`chat-train-toggle ${trainMode ? "chat-train-active" : ""}`}
                  onClick={() => (trainMode ? exitTrainMode() : startTrainMode())}
                  title={trainMode ? "Back to chat" : "Teach DOGEMAX"}
                >
                  {trainMode ? "Chat" : "Teach"}
                </button>
                <button
                  className="chat-close-btn"
                  onClick={() => setIsOpen(false)}
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chat-message ${
                    msg.role === "user"
                      ? "chat-message-user"
                      : "chat-message-bot"
                  }`}
                >
                  {msg.role === "assistant" && (
                    <div className="chat-msg-avatar">
                      <img src="/img/dogecoin-logo.png" alt="DOGEMAX" />
                    </div>
                  )}
                  <div className="chat-bubble">
                    <p>{msg.content}</p>
                    {msg.source === "trained" && (
                      <span className="chat-source-tag">Learned</span>
                    )}
                    {msg.source === "trained_new" && (
                      <span className="chat-source-tag chat-source-new">Learned!</span>
                    )}
                    {msg.source === "train_prompt" && (
                      <span className="chat-source-tag chat-source-teach">Teaching</span>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-message chat-message-bot">
                  <div className="chat-msg-avatar">
                    <img src="/img/dogecoin-logo.png" alt="DOGEMAX" />
                  </div>
                  <div className="chat-bubble">
                    <div className="chat-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              {trainMode && (
                <div className="chat-train-indicator">
                  Teaching mode {trainStep === "answer" ? `· answering "${trainTrigger}"` : "· waiting for topic"}
                </div>
              )}
              <div className="chat-input-row">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={
                    trainMode
                      ? trainStep === "answer"
                        ? "Type the answer DOGEMAX should give..."
                        : "What topic should DOGEMAX learn about?"
                      : "Ask DOGEMAX anything about Dogecoin..."
                  }
                  maxLength={500}
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  className="chat-send-btn"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 2L11 13" />
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="chat-footer">
              {stats && (
                <span className="chat-stats-inline">
                  {stats.facts} facts · {stats.trainedResponses} learned
                </span>
              )}
              <span>Powered by Trac Intercom</span>
            </div>
          </div>
        </div>
      )}

      <button
        className={`chat-fab ${isOpen ? "chat-fab-hidden" : ""}`}
        onClick={() => setIsOpen(true)}
        title="Chat with DOGEMAX"
      >
        <img src="/img/dogecoin-logo.png" alt="Chat" />
      </button>
    </section>
  );
}

export default ChatWithDogeMax;
